import React, { useState, useEffect, useCallback } from "react";
import { api, num } from "../lib/api";
import { toast } from "sonner";
import { Card, Button, Modal, Field, Input, Select } from "./shared";
import { Download, Upload, Trash2, Check, CheckCheck, Pencil } from "lucide-react";

export const BulkWipUpload = () => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [errors, setErrors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [jobbers, setJobbers] = useState([]);
  const [procNames, setProcNames] = useState([]);
  const [editForm, setEditForm] = useState(null);

  const loadDrafts = useCallback(async () => {
    try {
      const res = await api.get("/wip/drafts");
      setDrafts(res.data || []);
    } catch { /* not admin / no drafts */ }
  }, []);

  useEffect(() => {
    loadDrafts();
    api.get("/jobbers").then((r) => setJobbers(r.data || [])).catch(() => {});
    api.get("/processes").then((r) => setProcNames((r.data || []).filter((p) => p.active !== false).map((p) => p.name))).catch(() => {});
  }, [loadDrafts]);

  const processOptions = [...procNames, "Completed", "Fabric"];

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/wip/upload-template", { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u; a.download = "WIP_Upload_Template.xlsx"; a.click();
      URL.revokeObjectURL(u);
    } catch { toast.error("Download failed"); }
  };

  const upload = async () => {
    if (!file) { toast.error("Choose an Excel file first"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true); setSummary(null);
    try {
      const res = await api.post("/wip/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setErrors(res.data.errors || []);
      if (res.data.added) toast.success(`${res.data.added} rows added to preview — review, then Save`);
      if (res.data.rejected) toast(`${res.data.rejected} rows rejected — see details below`);
      setFile(null);
      await loadDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const saveRow = async (d) => {
    setSavingId(d.id);
    try {
      await api.post(`/wip/drafts/${d.id}/save`);
      toast.success(`Saved ${d.design} (${num(d.qty)} pcs) into production stock`);
      await loadDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSavingId(null); }
  };

  const deleteRow = async (d) => {
    if (!window.confirm(`Remove ${d.design} (${num(d.qty)} pcs) from the upload preview? This only removes the draft — no production data is affected.`)) return;
    try {
      await api.delete(`/wip/drafts/${d.id}`);
      toast.success("Draft row removed");
      await loadDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const saveAll = async () => {
    if (!drafts.length) return;
    if (!window.confirm(`Save all ${drafts.length} preview rows into production stock? This cannot be undone from here.`)) return;
    setSavingAll(true); setSummary(null);
    try {
      const res = await api.post("/wip/drafts/save-all");
      const rejected = res.data.errors?.length || 0;
      setSummary({ saved: res.data.saved || 0, rejected });
      setErrors(res.data.errors || []);
      toast.success(`Saved ${res.data.saved} rows into production stock`);
      if (rejected) toast(`${rejected} rows could not be saved — see details`);
      await loadDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Save All failed"); }
    finally { setSavingAll(false); }
  };

  const clearAll = async () => {
    if (!window.confirm("Discard ALL unsaved preview rows? Saved production data is not affected.")) return;
    try {
      await api.delete("/wip/drafts");
      toast.success("Preview cleared");
      setErrors([]); setSummary(null);
      await loadDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openEdit = (d) => setEditForm({
    id: d.id, design: d.design || "", colour: d.colour || "", item: d.item || "", qty: d.qty,
    process: d.process || "", type: d.is_jobber ? "Jobber" : "In-House", jobber_id: d.jobber_id || "",
    rate: d.rate || "", remarks: d.remarks || "",
  });

  const submitEdit = async (e) => {
    e.preventDefault();
    const f = editForm;
    try {
      await api.put(`/wip/drafts/${f.id}`, {
        design: f.design, colour: f.colour, item: f.item, qty: f.qty, process: f.process,
        type: f.type, jobber_id: f.jobber_id, rate: f.rate, remarks: f.remarks,
      });
      toast.success("Draft row updated");
      setEditForm(null);
      await loadDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const rowKind = (d) => d.is_fab ? "Fabric" : d.is_fin ? "Finished" : (d.is_jobber ? "Jobber" : "In-House");
  const specialProc = editForm && (["completed", "complete", "finished", "fabric", "fabric order"].includes((editForm.process || "").toLowerCase()));

  return (
    <Card className="p-5 mb-6">
      <p className="text-sm font-semibold text-slate-800 mb-1 font-head">Bulk WIP / Stage Stock Upload</p>
      <p className="text-xs text-slate-400 mb-4">Upload existing opening/current stock at any process stage. Uploaded rows first appear as a <b>preview / draft</b> below — nothing enters production stock until you press <b>Save</b> or <b>Save All</b>. You can edit or delete any row before saving. Drafts stay until you save, delete, or log out.</p>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" onClick={downloadTemplate} data-testid="wip-template-btn"><Download size={15} /> Download Template</Button>
        <input type="file" accept=".xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); }} className="text-sm" data-testid="wip-upload-file" />
        <Button onClick={upload} disabled={busy} data-testid="wip-upload-btn"><Upload size={15} /> {busy ? "Uploading…" : "Upload to Preview"}</Button>
      </div>

      {summary && (
        <div className="mt-4 flex gap-3" data-testid="wip-save-summary">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5">
            <CheckCheck size={15} /> Saved {summary.saved}
          </span>
          <span className={`inline-flex items-center gap-1.5 text-sm font-medium rounded-md px-3 py-1.5 border ${summary.rejected ? "text-red-700 bg-red-50 border-red-200" : "text-slate-500 bg-slate-50 border-slate-200"}`}>
            Rejected {summary.rejected}
          </span>
        </div>
      )}

      {errors.length > 0 && (
        <div className="mt-4 border border-red-200 rounded-md overflow-hidden" data-testid="wip-upload-errors">
          <p className="bg-red-50 text-red-700 px-3 py-1.5 text-sm font-medium">Rows not saved</p>
          <table className="w-full text-sm">
            <thead className="bg-red-50 text-red-700"><tr><th className="px-3 py-1.5 text-left w-20">Row</th><th className="px-3 py-1.5 text-left">Reason</th></tr></thead>
            <tbody>{errors.map((e, i) => (
              <tr key={i} className="border-t border-red-100"><td className="px-3 py-1.5 tabular">{e.row}</td><td className="px-3 py-1.5 text-slate-700">{e.reason}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {drafts.length > 0 && (
        <div className="mt-5" data-testid="wip-draft-preview">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-slate-700">Preview — {drafts.length} unsaved row{drafts.length > 1 ? "s" : ""}</p>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={clearAll} data-testid="wip-draft-clear-all"><Trash2 size={14} /> Discard All</Button>
              <Button size="sm" onClick={saveAll} disabled={savingAll} data-testid="wip-draft-save-all"><CheckCheck size={14} /> {savingAll ? "Saving…" : "Save All"}</Button>
            </div>
          </div>
          <div className="border border-slate-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
                <tr>
                  {["Row", "Design", "Colour", "Item", "Qty", "Process", "Type", "Jobber", "Actions"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {drafts.map((d) => (
                  <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`wip-draft-row-${d.id}`}>
                    <td className="px-3 py-2 tabular text-slate-400">{d.row}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">{d.design}</td>
                    <td className="px-3 py-2">{d.colour || "—"}</td>
                    <td className="px-3 py-2">{d.item || "—"}</td>
                    <td className="px-3 py-2 tabular font-semibold text-blue-800">{num(d.qty)}</td>
                    <td className="px-3 py-2">{d.process}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded ${d.is_fin ? "bg-emerald-50 text-emerald-700" : d.is_fab ? "bg-amber-50 text-amber-700" : d.is_jobber ? "bg-violet-50 text-violet-700" : "bg-sky-50 text-sky-700"}`}>{rowKind(d)}</span>
                    </td>
                    <td className="px-3 py-2">{d.jobber_name || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(d)} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded" data-testid={`wip-draft-edit-${d.id}`}><Pencil size={13} /> Edit</button>
                        <button onClick={() => saveRow(d)} disabled={savingId === d.id} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 rounded disabled:opacity-50" data-testid={`wip-draft-save-${d.id}`}><Check size={13} /> {savingId === d.id ? "Saving…" : "Save"}</button>
                        <button onClick={() => deleteRow(d)} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-50 rounded" data-testid={`wip-draft-delete-${d.id}`}><Trash2 size={13} /> Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400 mt-2">Edit and Delete are available only here, before saving. Once a row is saved it becomes permanent production data and cannot be deleted from the process screens.</p>
        </div>
      )}

      <Modal open={!!editForm} onClose={() => setEditForm(null)} title="Edit Preview Row">
        {editForm && (
          <form onSubmit={submitEdit} className="grid grid-cols-2 gap-4">
            <Field label="Design Number" required><Input value={editForm.design} onChange={(e) => setEditForm({ ...editForm, design: e.target.value })} required data-testid="wip-draft-edit-design" /></Field>
            <Field label="Quantity" required><Input type="number" step="any" value={editForm.qty} onChange={(e) => setEditForm({ ...editForm, qty: e.target.value })} required data-testid="wip-draft-edit-qty" /></Field>
            <Field label="Colour"><Input value={editForm.colour} onChange={(e) => setEditForm({ ...editForm, colour: e.target.value })} data-testid="wip-draft-edit-colour" /></Field>
            <Field label="Item Type"><Input value={editForm.item} onChange={(e) => setEditForm({ ...editForm, item: e.target.value })} data-testid="wip-draft-edit-item" /></Field>
            <Field label="Process / Stage" required>
              <Select value={editForm.process} onChange={(e) => setEditForm({ ...editForm, process: e.target.value })} required placeholder="Select stage" options={processOptions} data-testid="wip-draft-edit-process" />
            </Field>
            <Field label="Type">
              <Select value={editForm.type} onChange={(e) => setEditForm({ ...editForm, type: e.target.value })} options={["In-House", "Jobber"]} disabled={specialProc} data-testid="wip-draft-edit-type" />
            </Field>
            {editForm.type === "Jobber" && !specialProc && (
              <Field label="Jobber" required>
                <Select value={editForm.jobber_id} onChange={(e) => setEditForm({ ...editForm, jobber_id: e.target.value })} required placeholder="Select jobber" options={(jobbers || []).map((j) => ({ value: j.id, label: j.name }))} data-testid="wip-draft-edit-jobber" />
              </Field>
            )}
            <Field label="Rate"><Input type="number" step="any" value={editForm.rate} onChange={(e) => setEditForm({ ...editForm, rate: e.target.value })} data-testid="wip-draft-edit-rate" /></Field>
            <Field label="Remarks" className="col-span-2"><Input value={editForm.remarks} onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })} data-testid="wip-draft-edit-remarks" /></Field>
            {specialProc && <p className="col-span-2 text-xs text-slate-400">This is a {editForm.process} row — Jobber/In-House type does not apply.</p>}
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setEditForm(null)}>Cancel</Button>
              <Button type="submit" data-testid="wip-draft-edit-save">Save Changes</Button>
            </div>
          </form>
        )}
      </Modal>
    </Card>
  );
};
